import { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "./site/site.css";

import { useTheme, useLang, prefersReduced } from "./site/hooks";
import CanvasBackground from "./site/CanvasBackground";
import Cursor from "./site/Cursor";
import Header from "./site/Header";
import Home from "./site/Home";
import Adhesion from "./site/Adhesion";
import AdhesionConfirmee from "./site/AdhesionConfirmee";

function App() {
  const { theme, toggle: toggleTheme } = useTheme();
  const { lang, toggle: toggleLang } = useLang();
  const location = useLocation();

  // Smooth momentum scrolling (disabled when reduced motion is requested).
  useEffect(() => {
    if (prefersReduced) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  // Play the masked line-reveal on mount (body.loaded gates .line-mask spans).
  useEffect(() => {
    const id = requestAnimationFrame(() => document.body.classList.add("loaded"));
    return () => cancelAnimationFrame(id);
  }, []);

  // Reset scroll to top on route change (unless navigating to a hash anchor).
  useEffect(() => {
    if (window.location.hash) return;
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname]);

  // Scroll reveals - native IntersectionObserver + mandatory safety net.
  // Re-runs on each route change so newly mounted sections reveal correctly.
  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.is-in)");
    if (prefersReduced) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    // Safety net: never leave text invisible.
    const safety = setTimeout(() => {
      document
        .querySelectorAll(".reveal:not(.is-in)")
        .forEach((el) => el.classList.add("is-in"));
    }, 2500);
    return () => {
      io.disconnect();
      clearTimeout(safety);
    };
  }, [location.pathname]);

  return (
    <div className="App">
      <CanvasBackground theme={theme} />
      <div className="bg-grid" aria-hidden="true" />
      <div className="bg-vignette" aria-hidden="true" />
      <Cursor />

      <Header
        lang={lang}
        toggleLang={toggleLang}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <Routes>
        <Route path="/" element={<Home lang={lang} />} />
        <Route path="/adhesion" element={<Adhesion lang={lang} />} />
        <Route path="/adhesion/confirmee" element={<AdhesionConfirmee lang={lang} />} />
      </Routes>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "var(--ink-2)",
            color: "var(--fg)",
            border: "1px solid rgb(var(--line) / 0.2)",
            borderRadius: "2px",
            fontFamily: "var(--f-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.04em",
          },
        }}
      />
    </div>
  );
}

export default App;
